import styled, { css } from 'styled-components';

import Filter from 'common/components/molecules/Filter';
import { getPartnerPrimaryColor } from 'common/styles/organisationPartnerTheme';

// Brand accent for selection/hover/focus/links. Resolves per customer (Petasense
// green, NTN's colour, or an explicit partner primaryColor) — never a hard-coded
// green, which would show through on non-Petasense brands.
const primaryColor = theme => getPartnerPrimaryColor(theme);

// Shared keyboard focus ring. Only shows for keyboard users (:focus-visible),
// so mouse clicks stay clean while Tab navigation is clearly visible — the
// panel had no visible focus state at all before.
const focusRing = css`
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${props => primaryColor(props.theme)};
    border-radius: 4px;
  }
`;

// Row that holds the two pills + the `›` separator, sitting between the
// hierarchy toolbar and the node tree. Never allowed to overflow the panel.
// position:relative so the pill dropdowns (which we detach from their own pill,
// see OrgFilter/SiteFilter) anchor to the panel's left edge, not the pill.
export const PillRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  position: relative;
  width: 100%;
  min-width: 0;
  padding: 10px;
  box-sizing: border-box;
  border-top: 1px solid ${props => props.theme.colors.greyL};
  border-bottom: 1px solid ${props => props.theme.colors.greyL};
`;

export const Separator = styled.span`
  margin: 0 4px;
  color: ${props => props.theme.colors.greyD};
  font-size: 11px;
  flex-shrink: 0;
`;

// Filter wrappers: let each pill shrink inside the panel. The org pill keeps
// its content width but can shrink; the site pill takes the remaining room.
// NB: no overflow:hidden here — the dropdown popover renders inside the
// Filter container, so clipping it would hide the dropdown. Shrink-to-fit is
// handled by flex + min-width:0 on these wrappers and ellipsis on PillText.
// The Filter's inner value container (.filter-item) defaults to min-width:auto,
// which stops the pill shrinking and lets it overflow the pane — force it to 0
// so the ellipsis chain reaches PillText.
// position:static makes the absolutely-positioned dropdown skip the pill and
// anchor to PillRow instead, so both dropdowns open at the panel's left edge
// regardless of which pill was clicked.
export const OrgFilter = styled(Filter)`
  position: static;
  flex: 0 1 auto;
  min-width: 0;

  .filter-item {
    min-width: 0;
    max-width: 100%;
  }
`;

export const SiteFilter = styled(Filter)`
  position: static;
  flex: 1 1 auto;
  min-width: 0;

  .filter-item {
    min-width: 0;
    max-width: 100%;
  }
`;

// The clickable rounded pill (rendered as the Filter's customDisplayValue).
// Compact type so the switcher reads neat against the denser hierarchy below.
// `muted` = org-level return context: the site you'd go back to, shown quieter
// than an active selection so it reads as breadcrumb context, not current state.
export const Pill = styled.div`
  display: flex;
  align-items: center;
  max-width: 100%;
  min-width: 0;
  padding: 3px 7px;
  border: 1px solid ${props => props.theme.colors.greyL};
  border-radius: 12px;
  background: ${props => (props.muted ? props.theme.colors.greyXXXL : props.theme.colors.white)};
  cursor: pointer;
  user-select: none;
  overflow: hidden;

  &:hover {
    border-color: ${props => props.theme.colors.borderGrayHover};
  }

  ${focusRing}

  svg {
    flex-shrink: 0;
    color: ${props => props.theme.colors.greyXD};
  }
`;

export const PillIcon = styled.span`
  display: inline-flex;
  margin-right: 5px;
  color: ${props => props.theme.colors.greyXD};
  font-size: 10px;
  flex-shrink: 0;
`;

export const PillText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 5px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.3;
  color: ${props => (props.muted ? props.theme.colors.greyD : props.theme.colors.greyXXD)};
`;

// Small trailing dropdown caret. Sized via font-size (icon is 1em) so it stays
// compact and comfortable inside the pill instead of the oversized FA default.
export const PillChevron = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  font-size: 8px;
  color: ${props => props.theme.colors.greyD};
`;

// Org name rendered as a direct link to the org dashboard (role-gated). Clicking
// it navigates; the separate chevron opens the switcher. The arrow + hover
// underline are the affordance that makes the org dashboard discoverable.
export const PillLink = styled.span`
  display: flex;
  align-items: center;
  min-width: 0;
  cursor: pointer;
  outline: none;

  &:hover .pill-name,
  &:focus-visible .pill-name {
    color: ${props => primaryColor(props.theme)};
    text-decoration: underline;
  }
  &:hover svg,
  &:focus-visible svg {
    color: ${props => primaryColor(props.theme)};
  }
`;

// Non-interactive context label: shown when there is nothing to navigate to or
// switch between (single site / no dashboard access). No border, no caret — its
// flatness is the signal that it is not a control.
export const PillLabel = styled.div`
  display: flex;
  align-items: center;
  max-width: 100%;
  min-width: 0;
  padding: 3px 7px;

  svg {
    flex-shrink: 0;
    color: ${props => props.theme.colors.greyXD};
  }
`;

// Flex wrappers for the static (non-dropdown) variants, mirroring the sizing of
// OrgFilter / SiteFilter so ellipsis behaves the same whether or not the
// segment is a dropdown.
export const OrgStatic = styled.div`
  flex: 0 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
`;

export const SiteStatic = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
`;

// Expand/collapse caret for an org group inside the switcher. A real button so
// it is keyboard-reachable; hit area separated from the org-navigate click.
export const OrgCaret = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-right: 2px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${props => props.theme.colors.greyD};
  cursor: pointer;
  font-size: 9px;
  visibility: ${props => (props.hidden ? 'hidden' : 'visible')};

  &:hover {
    color: ${props => props.theme.colors.greyXXD};
  }

  ${focusRing}
`;

// Dropdown internals -------------------------------------------------------

// Org group header inside the dropdown: icon + name + site count.
export const OrgGroupHeader = styled.div`
  display: flex;
  align-items: center;
  min-width: 220px;
  padding: 5px 10px 2px 6px;
  font-size: 11px;
  font-weight: 700;
  color: ${props => (props.selected ? props.theme.colors.greyXXD : props.theme.colors.greyXD)};

  .name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: ${props => (props.clickable ? 'pointer' : 'default')};
  }

  .name[role="button"]:hover,
  .name[role="button"]:focus-visible {
    color: ${props => primaryColor(props.theme)};
    outline: none;
    text-decoration: underline;
  }

  svg.org-icon {
    margin-right: 6px;
    flex-shrink: 0;
    color: ${props => props.theme.colors.greyXD};
  }
`;

export const Count = styled.span`
  margin-left: 8px;
  font-size: 10px;
  font-weight: 600;
  color: ${props => props.theme.colors.greyD};
`;

// Org row in the org-pill switcher (Option B): a flat list of orgs, each a place
// you navigate to. No nested sites — those live on the site pill. The trailing
// count is the org's site total, so multi-org users still gauge scale at a glance.
export const OrgRow = styled.div`
  display: flex;
  align-items: center;
  min-width: 220px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
  color: ${props => (props.selected ? primaryColor(props.theme) : props.theme.colors.greyXD)};
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};

  .text {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg.org-icon {
    margin-right: 8px;
    flex-shrink: 0;
    color: ${props => (props.selected ? primaryColor(props.theme) : props.theme.colors.greyXD)};
  }

  &:hover {
    background: ${props => (props.clickable ? props.theme.colors.greyXXXL : 'transparent')};
    color: ${props => (props.selected ? primaryColor(props.theme) : props.theme.colors.greyXXD)};
  }

  &:focus-visible {
    outline: none;
    background: ${props => props.theme.colors.greyXXXL};
    box-shadow: inset 2px 0 0 ${props => primaryColor(props.theme)};
  }
`;

// Site row nested under an org header.
export const SiteRow = styled.div`
  display: flex;
  align-items: center;
  min-width: 220px;
  padding: 4px 10px 4px 28px;
  font-size: 12px;
  font-weight: 600;
  color: ${props => (props.selected ? primaryColor(props.theme) : props.theme.colors.greyXD)};
  cursor: pointer;

  .text {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    background: ${props => props.theme.colors.greyXXXL};
    color: ${props => (props.selected ? primaryColor(props.theme) : props.theme.colors.greyXXD)};
  }

  &:focus-visible {
    outline: none;
    background: ${props => props.theme.colors.greyXXXL};
    box-shadow: inset 2px 0 0 ${props => primaryColor(props.theme)};
  }
`;

export const CheckMark = styled.span`
  display: inline-flex;
  margin-left: 8px;
  color: ${props => primaryColor(props.theme)};
  font-size: 11px;
`;

export const SectionLabel = styled.div`
  padding: 5px 10px 2px 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.greyD};
`;

export const ManageFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 220px;
  padding: 6px 10px;
  border-top: 1px solid ${props => props.theme.colors.greyXL};
  font-size: 12px;
  font-weight: 600;
  color: ${props => primaryColor(props.theme)};
  cursor: pointer;

  &:hover {
    background: ${props => props.theme.colors.greyXXXL};
  }

  ${focusRing}
`;

export const EmptyMsg = styled.div`
  min-width: 220px;
  padding: 8px 10px;
  font-size: 12px;
  color: ${props => props.theme.colors.greyD};
`;
